import React, { useCallback, useEffect, useState } from 'react';
import { Download, Bell, BellOff, X, Share, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  onInstallAvailable,
  promptInstall,
  isStandalone,
  isIOS,
  getPushState,
  subscribeToPush,
  unsubscribeFromPush,
  getPushPreferences,
  setPushPreferences,
} from '@/lib/pwa';
import { useAuth } from '@/context/AuthContext';

const DISMISS_KEY_INSTALL = 'moj-install-dismissed-at';
const DISMISS_KEY_PUSH = 'moj-push-dismissed-at';
// Re-show a dismissed banner after a week — long enough that it doesn't nag,
// short enough that users who swipe it away by mistake will see it again.
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

function wasDismissedRecently(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_COOLDOWN_MS;
  } catch (_) { return false; }
}

function remember(key) {
  try { localStorage.setItem(key, String(Date.now())); } catch (_) { /* ignore */ }
}


// ---------- Install prompt ----------
// Behaviour:
//   • On Android/desktop Chromium we wait for the browser's `beforeinstallprompt`
//     event, which only fires when the PWA passes installability checks.
//   • On iOS we show a short "Add to Home Screen" sheet because Safari never
//     fires the event — users have to do it manually from the Share menu.
//   • Already-installed PWAs (standalone) never see the banner.
function InstallCard({ children, onClose, testid }) {
  return (
    <div
      role="dialog"
      aria-label="Install Ministry of Jesus"
      data-testid={testid}
      className="fixed inset-x-3 bottom-3 z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm card-moj p-4 pr-10 shadow-2xl border-moj-gold/40"
    >
      {children}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute top-2 right-2 p-1.5 rounded-full text-moj-charcoal/60 hover:bg-moj-cream touch-manipulation"
        data-testid={`${testid}-close`}
      >
        <X size={16} />
      </button>
    </div>
  );
}

function AndroidInstallBanner({ onClose }) {
  const handleInstall = useCallback(async () => {
    const choice = await promptInstall();
    if (choice.outcome === 'accepted') {
      toast.success("MoJ is being installed to your home screen.");
    } else if (choice.outcome === 'dismissed') {
      remember(DISMISS_KEY_INSTALL);
    }
    onClose();
  }, [onClose]);

  return (
    <InstallCard onClose={onClose} testid="pwa-install-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-moj-purple-deep text-moj-gold flex items-center justify-center flex-shrink-0">
          <Download size={20} />
        </div>
        <div className="min-w-0">
          <div className="font-serif text-moj-purple-deep text-base leading-tight">Install Ministry of Jesus</div>
          <p className="text-xs text-moj-charcoal/80 mt-1 leading-relaxed">
            Add MoJ to your home screen for a full-screen, app-like experience with offline reading and notifications.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={handleInstall}
              className="btn-primary text-xs py-1.5 px-4"
              data-testid="pwa-install-accept"
            >
              <Download size={14} /> Install
            </button>
            <button
              type="button"
              onClick={() => { remember(DISMISS_KEY_INSTALL); onClose(); }}
              className="text-xs text-moj-charcoal/70 hover:text-moj-purple-deep underline"
              data-testid="pwa-install-later"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </InstallCard>
  );
}

function IosInstallBanner({ onClose }) {
  return (
    <InstallCard onClose={onClose} testid="pwa-ios-install-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-moj-purple-deep text-moj-gold flex items-center justify-center flex-shrink-0">
          <Share size={20} />
        </div>
        <div className="min-w-0">
          <div className="font-serif text-moj-purple-deep text-base leading-tight">Add MoJ to your Home Screen</div>
          <p className="text-xs text-moj-charcoal/80 mt-1 leading-relaxed">
            Tap <span className="inline-flex items-center align-middle"><Share size={12} className="mx-0.5" /></span>
            in Safari's share menu, then choose <span className="inline-flex items-center align-middle"><Plus size={12} className="mx-0.5" /></span>
            <span className="font-semibold">Add to Home Screen</span>.
          </p>
          <button
            type="button"
            onClick={() => { remember(DISMISS_KEY_INSTALL); onClose(); }}
            className="text-xs text-moj-charcoal/70 hover:text-moj-purple-deep underline mt-3"
            data-testid="pwa-ios-install-later"
          >
            Got it
          </button>
        </div>
      </div>
    </InstallCard>
  );
}


// ---------- Push opt-in ----------
// We offer this only once the service worker is live AND the user has been on
// the site long enough to feel invested (either they're on the second page in
// this session or they already have an account). It's dismissable with the
// same 7-day cooldown as install.
function PushBanner({ onClose, userId }) {
  const [busy, setBusy] = useState(false);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      await subscribeToPush({ userId });
      toast.success('Notifications enabled. We’ll ping you when the live service goes on and when new leaflets are published.');
      remember(DISMISS_KEY_PUSH);
      onClose();
    } catch (e) {
      if (e?.code === 'DENIED') {
        toast.error('Permission denied. You can enable notifications later from your browser settings.');
      } else if (e?.code === 'IOS_INSTALL_REQUIRED') {
        toast.message('On iPhone/iPad, add MoJ to your home screen first, then open it from there to enable notifications.');
      } else {
        toast.error(e?.message || 'Could not enable notifications.');
      }
    } finally {
      setBusy(false);
    }
  }, [userId, onClose]);

  return (
    <InstallCard onClose={onClose} testid="pwa-push-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-moj-purple-deep text-moj-gold flex items-center justify-center flex-shrink-0">
          <Bell size={20} />
        </div>
        <div className="min-w-0">
          <div className="font-serif text-moj-purple-deep text-base leading-tight">Stay in the loop</div>
          <p className="text-xs text-moj-charcoal/80 mt-1 leading-relaxed">
            Get a gentle ping when the Sunday service goes LIVE, new leaflets are published, or there's a prayer update.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={enable}
              disabled={busy}
              className="btn-primary text-xs py-1.5 px-4 disabled:opacity-60"
              data-testid="pwa-push-enable"
            >
              <Bell size={14} /> {busy ? 'Enabling…' : 'Enable notifications'}
            </button>
            <button
              type="button"
              onClick={() => { remember(DISMISS_KEY_PUSH); onClose(); }}
              className="text-xs text-moj-charcoal/70 hover:text-moj-purple-deep underline"
              data-testid="pwa-push-later"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </InstallCard>
  );
}


// ---------- Compound controller ----------
export default function PwaPrompts() {
  const { user } = useAuth() || {};
  const [showInstall, setShowInstall] = useState(false);
  const [showIosInstall, setShowIosInstall] = useState(false);
  const [showPush, setShowPush] = useState(false);

  // Android / desktop beforeinstallprompt
  useEffect(() => {
    if (isStandalone()) return undefined;
    if (wasDismissedRecently(DISMISS_KEY_INSTALL)) return undefined;
    const off = onInstallAvailable((evt) => {
      setShowInstall(Boolean(evt));
    });
    return off;
  }, []);

  // iOS fallback: show after 20s on the public site, only once per cooldown.
  useEffect(() => {
    if (!isIOS() || isStandalone()) return undefined;
    if (wasDismissedRecently(DISMISS_KEY_INSTALL)) return undefined;
    const t = setTimeout(() => setShowIosInstall(true), 20_000);
    return () => clearTimeout(t);
  }, []);

  // Push opt-in: wait a bit so we don't ambush the first page view, and
  // only show if the browser actually supports push and permission is "default".
  useEffect(() => {
    if (wasDismissedRecently(DISMISS_KEY_PUSH)) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      const st = await getPushState();
      if (cancelled) return;
      if (!st.supported) return;
      if (st.permission !== 'default') return;
      if (st.subscribed) return;
      setShowPush(true);
    }, 15_000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [user?.id]);

  return (
    <>
      {showInstall && <AndroidInstallBanner onClose={() => setShowInstall(false)} />}
      {!showInstall && showIosInstall && <IosInstallBanner onClose={() => setShowIosInstall(false)} />}
      {!showInstall && !showIosInstall && showPush && (
        <PushBanner onClose={() => setShowPush(false)} userId={user?.id || null} />
      )}
    </>
  );
}


// ---------- Standalone toggle (used in member Profile / admin tab) ----------
export function PushToggleButton({ userId, className = '' }) {
  const [state, setState] = useState({ supported: false, subscribed: false, permission: 'default' });
  const [busy, setBusy] = useState(false);
  // Per-channel preferences fetched when we know we're subscribed. Null until
  // either the backend returns 404 (no sub record) or the real values arrive.
  const [prefs, setPrefs] = useState(null);
  const [prefsBusy, setPrefsBusy] = useState(false);

  const refresh = useCallback(async () => {
    const st = await getPushState();
    setState(st);
    // Load prefs only when subscribed; otherwise there's nothing to toggle.
    if (st.subscribed) {
      try {
        const p = await getPushPreferences();
        setPrefs(p); // may be null if backend has no record
      } catch (_) { setPrefs(null); }
    } else {
      setPrefs(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleToggle = useCallback(async () => {
    setBusy(true);
    try {
      if (state.subscribed) {
        await unsubscribeFromPush();
        toast.success('Notifications turned off.');
      } else {
        await subscribeToPush({ userId });
        toast.success('Notifications turned on.');
      }
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'Could not update notifications.');
    } finally {
      setBusy(false);
    }
  }, [state.subscribed, userId, refresh]);

  const togglePrayerPings = useCallback(async (nextValue) => {
    setPrefsBusy(true);
    try {
      await setPushPreferences({ pray_pings_enabled: nextValue });
      setPrefs((prev) => ({ ...(prev || {}), pray_pings_enabled: nextValue }));
      toast.success(nextValue
        ? "You'll hear from us when the family is called to prayer."
        : "Prayer pings turned off. You'll still get other notifications.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update preferences.');
    } finally {
      setPrefsBusy(false);
    }
  }, []);

  if (!state.supported) {
    if (state.iosRequiresInstall) {
      return (
        <p className={`text-xs text-moj-charcoal/70 ${className}`} data-testid="push-ios-install-note">
          Add MoJ to your Home Screen first, then open it from there to enable push notifications.
        </p>
      );
    }
    return (
      <p className={`text-xs text-moj-charcoal/60 ${className}`} data-testid="push-unsupported">
        Push notifications aren't supported in this browser.
      </p>
    );
  }

  if (state.permission === 'denied') {
    return (
      <p className={`text-xs text-moj-charcoal/70 ${className}`} data-testid="push-denied">
        Notifications are blocked in your browser settings. Allow them for this site to turn pings back on.
      </p>
    );
  }

  // When the user is NOT subscribed we just show the main enable button —
  // sub-preferences only make sense once a subscription exists.
  if (!state.subscribed) {
    return (
      <button
        type="button"
        onClick={handleToggle}
        disabled={busy}
        className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 min-h-[40px] rounded-full border transition touch-manipulation disabled:opacity-60 bg-moj-purple text-white border-moj-purple hover:brightness-110 ${className}`}
        data-testid="push-toggle"
      >
        <Bell size={16} />
        {busy ? '…' : 'Enable notifications'}
      </button>
    );
  }

  // Subscribed → show the main toggle PLUS the prayer-ping sub-toggle.
  const prayOn = prefs?.pray_pings_enabled !== false; // default true
  return (
    <div className={`flex flex-col sm:items-end gap-2 ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={busy}
        className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 min-h-[40px] rounded-full border transition touch-manipulation disabled:opacity-60 bg-moj-cream border-moj-gold/50 text-moj-purple-deep hover:bg-moj-gold/10"
        data-testid="push-toggle"
      >
        <BellOff size={16} />
        {busy ? '…' : 'Notifications on'}
      </button>
      <label
        className="flex items-center gap-2 text-xs text-moj-charcoal/80 cursor-pointer select-none"
        data-testid="push-prayer-pings-toggle"
      >
        <input
          type="checkbox"
          checked={prayOn}
          disabled={prefsBusy}
          onChange={(e) => togglePrayerPings(e.target.checked)}
          className="w-4 h-4 rounded border-moj-gold/40 text-moj-purple focus:ring-moj-gold accent-moj-purple"
          data-testid="push-prayer-pings-checkbox"
        />
        <span>Also ping me for urgent prayer calls</span>
      </label>
    </div>
  );
}
